import { Component, Input, OnInit } from '@angular/core';
import { GameSettingsService } from '../game-settings.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { HelperService } from '@app/main/core/services/helper.service';

@Component({
  selector: 'color-wheel-selection',
  templateUrl: './color-wheel-selection.component.html',
  styleUrls: ['./color-wheel-selection.component.scss']
})
export class ColorWheelSelectionComponent {
  colorWheels = [
    { type: 'img', label: 'Image' },
    { type: 'java', label: 'Java' },
    { type: 'css', label: 'CSS' }
  ];

  @Input('gameId') gameId: string = '';
  @Input('drawDate') drawDate: string = '';

  coinIconSrc: string | null = null;
  blankCoinIconSrc: string | null = null;

  constructor(
    private domSanitizer: DomSanitizer,
    public _gameSettingsService: GameSettingsService,
    private helperService: HelperService
  ) {
    
    let img: any = null;
    let src: any = null;

    if (this._gameSettingsService.colorWheel.data.img && this._gameSettingsService.colorWheel.data.img.file) {
      img = this._gameSettingsService.colorWheel.data.img.file;
    }

    if (this._gameSettingsService.colorWheel.data.img && this._gameSettingsService.colorWheel.data.img.src) {
      src = this._gameSettingsService.colorWheel.data.img.src;
    } else if (this._gameSettingsService.defaultColorRing !== null) {
      src = this._gameSettingsService.defaultColorRing.value;
    }

    this._gameSettingsService.colorWheel.data = {
      img: { file: img, src: src },
      color_1: this._gameSettingsService.colorWheel.data.color_1 && this.gameId !== '' ? this._gameSettingsService.colorWheel.data.color_1 : '#FFA500',
      color_2: this._gameSettingsService.colorWheel.data.color_2 && this.gameId !== '' ? this._gameSettingsService.colorWheel.data.color_2 : '#FFEF00',
      color_3: this._gameSettingsService.colorWheel.data.color_3 && this.gameId !== '' ? this._gameSettingsService.colorWheel.data.color_3 : '#00FF00',
      color_4: this._gameSettingsService.colorWheel.data.color_4 && this.gameId !== '' ? this._gameSettingsService.colorWheel.data.color_4 : '#0000FF',
      color_5: this._gameSettingsService.colorWheel.data.color_5 && this.gameId !== '' ? this._gameSettingsService.colorWheel.data.color_5 : '#800080',
      color_6: this._gameSettingsService.colorWheel.data.color_6 && this.gameId !== '' ? this._gameSettingsService.colorWheel.data.color_6 : '#FF0000',
      cssCenterChoice: this._gameSettingsService.colorWheel.data.cssCenterChoice || 'emoji' // Preserve existing value
    };

    if (this._gameSettingsService.greyCoinIcon && this._gameSettingsService.greyCoinIcon.value) {
      this.coinIconSrc = this.resolveSrc(this._gameSettingsService.greyCoinIcon.value);
    } else {
      this._gameSettingsService.getDefaultCoinIcons().then(icons => {
        if (icons && icons.points && icons.points.value) {
          this.coinIconSrc = this.resolveSrc(icons.points.value);
        }
      }).catch(() => {
        this.coinIconSrc = '/assets/images/image-not-found-150x150.png';
      });
    }

    if (this._gameSettingsService.blankCoinIcon && this._gameSettingsService.blankCoinIcon.value) {
      this.blankCoinIconSrc = this.resolveSrc(this._gameSettingsService.blankCoinIcon.value);
    } else {
      this._gameSettingsService.getDefaultCoinIcons().then(icons => {
        if (icons && icons.blankCoin && icons.blankCoin.value) {
          this.blankCoinIconSrc = this.resolveSrc(icons.blankCoin.value);
        }
      }).catch(() => {
        this.blankCoinIconSrc = '/assets/images/image-not-found-150x150.png';
      });
    }
  }
  getConicGradient() {
    const colors = [
      this._gameSettingsService.colorWheel.data.color_1 || 'red',
      this._gameSettingsService.colorWheel.data.color_2 || 'green',
      this._gameSettingsService.colorWheel.data.color_3 || 'blue',
      this._gameSettingsService.colorWheel.data.color_4 || '#0000FF',
      this._gameSettingsService.colorWheel.data.color_5 || '#800080',
      this._gameSettingsService.colorWheel.data.color_6 || '#FF0000'
    ];
    return `conic-gradient(${colors.join(', ')})`;
  }

  colorWheelUpload = async ($event: any) => {
    if ($event.target.files && $event.target.files.length) {
      try {
        const file = $event.target.files[0];
        
        // Store the original file for upload
        this._gameSettingsService.colorWheel.data.img.file = { target: { files: [file] } };
        
        // Create base64 preview from original file
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this._gameSettingsService.colorWheel.data.img.src = this.sanitizeUrl(e.target.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading color wheel image:', error);
      }
    }
  };

  sanitizeUrl = (url: any): SafeUrl => {
    return this.domSanitizer.bypassSecurityTrustUrl(url as string);
  }

  getImageSrc() {
    const img = this._gameSettingsService.colorWheel.data.img;
    if (img && img.src) {
      const base64Pattern = /^data:image\/(png|jpg|jpeg|gif);base64,/;
      const isBase64 = base64Pattern.test(img.src);
      if (isBase64 || (img.src as any).changingThisBreaksApplicationSecurity) {
        return img.src;
      } else {
        return environment.s3BucketUrl + img.src;
      }
    }
    return null;
  }

  onCssCenterSelect(choice: 'emoji' | 'greyCoin' | 'blankCoin') {
    this._gameSettingsService.colorWheel.data.cssCenterChoice = choice;
  }

  getCoinIconSrc(): string {
    return this.coinIconSrc || '/assets/images/image-not-found-150x150.png';
  }
  
  getBlankCoinIconSrc(): string {
    return '/assets/images/hundred-coin.PNG';
  }
  
  // Non-re-entrant: same guard as GameSettingsComponent.onImageError.
  onCoinError(event: any) {
    const img = event.target as HTMLImageElement;
    const fallback = '/assets/images/image-not-found-150x150.png';
    if (img.getAttribute('src') === fallback) {
      return;
    }
    img.src = fallback;
  }

  resolveSrc(value: string): string {
    if (!value) return '/assets/images/image-not-found-150x150.png';
    const v = String(value).trim();
    if (/^(data:|https?:|blob:)/i.test(v)) return v;
    if (v.startsWith('/assets/')) return v;
    return environment.s3BucketUrl + v;
  }
}
